/**
 * Pantalla de TRASPASO: lo unico que el comprador debe ver entre que abre el checkout y que aterriza
 * en la pasarela, cuando no hay nada que elegir.
 *
 * <h2>Que problema resuelve</h2>
 *
 * <p>Cuando el comercio crea el intent con la pasarela ya fijada, el comprador no pasa por el
 * selector: {@code CheckoutSessionService.restrictTo} deja un solo metodo y el mapeo de estados va
 * directo a {@code gateway_flow}. Aun asi, hasta el 2026-09-02 el camino se veia como cuatro
 * pantallas encadenadas, cada una de una altura distinta:
 *
 * <ol>
 *   <li>«Cargando tu pago…», mientras se resuelve el GET del intent;
 *   <li>«Preparando el medio de pago…», mientras se descarga el chunk de la estrategia;
 *   <li><strong>la pantalla de pago entera</strong> —titulo, aviso y el boton «Pagar con X»— que se
 *       pintaba un fotograma y acto seguido se deshabilitaba sola, porque el auto-arranque vive en
 *       un efecto pasivo y React ya habia pintado el estado {@code idle};
 *   <li>«Te estamos redirigiendo…», y la navegacion.
 * </ol>
 *
 * <p>El tercero es el que delataba el hub: un boton que aparece y se apaga solo se lee como «llegue
 * a una pantalla que no era para mi». Los otros tres son honestos pero se veian como saltos porque
 * cada uno tenia su propia forma.
 *
 * <p>Esta pantalla es la MISMA en los cuatro momentos. Lo unico que cambia es la frase, y la altura
 * esta reservada, asi que el panel no da un salto entre una y otra: el comprador ve una sola espera,
 * no una sucesion de pantallas.
 *
 * <h2>Lo que NO hace</h2>
 *
 * <p>No finge que el traspaso es instantaneo ni oculta que hay una espera: el `role="status"` la
 * anuncia y la frase dice a donde se va. Maquillar la espera no es esconderla; es que se vea como
 * una sola cosa.
 *
 * <p>Y no se usa cuando el comprador SI tiene algo que hacer aqui —el selector, el QR de RutPay, el
 * boton de reintentar tras un fallo—. Ahi la pantalla de pago es la correcta.
 */
import type { JSX } from 'react';

export interface HandoffScreenProps {
  /** Frase que describe la espera; ya localizada por quien la monta. */
  message: string;
  /**
   * `data-testid` del bloque. Por defecto `handoff-screen`; la carcasa conserva
   * `loading-indicator`, que es el nombre con el que ya se la conocia.
   */
  testId?: string | undefined;
}

/*
 * Altura reservada. Es lo que impide el salto entre los cuatro momentos del traspaso: sin ella, el
 * bloque mide lo que mida su frase y el panel se encoge y se estira en cada cambio.
 *
 * 11rem es la altura del bloque mas alto de la secuencia (spinner + dos lineas de texto en movil);
 * de sobra para una linea y sin dejar un hueco visible en escritorio.
 */
const HANDOFF_CLASS =
  'flex min-h-[11rem] flex-col items-center justify-center gap-3 py-8 text-center';

/*
 * `motion-reduce:animate-none` no es un detalle de accesibilidad de compromiso: un spinner es lo
 * unico que se mueve en la pantalla, y para quien pidio menos movimiento un circulo girando durante
 * varios segundos es exactamente lo que pidio no ver. Sin animacion sigue siendo un indicador
 * valido, porque el texto ya dice que se esta esperando.
 */
const SPINNER_CLASS =
  'h-7 w-7 animate-spin motion-reduce:animate-none text-[var(--apipay-color-primary,#3a4fd9)]';

export function HandoffScreen({ message, testId }: HandoffScreenProps): JSX.Element {
  return (
    <div
      className={HANDOFF_CLASS}
      data-testid={testId ?? 'handoff-screen'}
      role="status"
      aria-live="polite"
    >
      <svg
        className={SPINNER_CLASS}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        {/* La pista completa, tenue: da el circulo de referencia sobre el que se lee el arco. */}
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.18" />
        {/* Un cuarto de vuelta con la punta redondeada; es lo unico que el ojo sigue al girar. */}
        <path
          d="M22 12a10 10 0 0 0-10-10"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      <p className="text-sm text-[var(--apipay-color-muted,#64748b)]">{message}</p>
    </div>
  );
}
